import { useCallback, useEffect, useState } from 'react';
import { getCached, setCached, CACHE_TTL } from '../utils/cache';
import { reverseGeocode } from '../services/geocodingService';
import { getProjectLocations } from '../services/attendanceApi';

export function useLocation() {
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [locationPermission, setLocationPermission] = useState('prompt');
  const [isRequestingLocation, setIsRequestingLocation] = useState(false);
  const [projectLocations, setProjectLocations] = useState([]);

  const loadProjectLocations = useCallback(async () => {
    try {
      const locations = await getProjectLocations();
      setProjectLocations(locations || []);
    } catch {
      setProjectLocations([]);
    }
  }, []);

  const resolvePosition = useCallback(async (coords) => {
    const locationData = {
      latitude: coords.latitude,
      longitude: coords.longitude,
      address: '',
      accuracy: coords.accuracy || null,
    };

    setLocation(locationData);
    setCached('lastLocation', locationData);

    try {
      const address = await reverseGeocode(coords.latitude, coords.longitude);
      const updated = { ...locationData, address: address || '' };
      setLocation(updated);
      setCached('lastLocation', updated);
    } catch {
      // Keep coordinates-only location on geocoding failures.
    }
  }, []);

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError('Trình duyệt không hỗ trợ định vị.');
      setLocationPermission('denied');
      return;
    }

    setIsRequestingLocation(true);
    setLocationError(null);

    const cached = getCached('lastLocation', CACHE_TTL.LOCATION);
    if (cached?.latitude && cached?.longitude) {
      setLocation(cached);
      setLocationPermission('granted');
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        setLocationPermission('granted');
        await resolvePosition(position.coords);
        setIsRequestingLocation(false);
      },
      (error) => {
        setIsRequestingLocation(false);
        if (error.code === error.PERMISSION_DENIED) {
          setLocationPermission('denied');
          setLocationError('Bạn đã từ chối quyền truy cập vị trí. Vui lòng cấp quyền và thử lại.');
          return;
        }
        if (error.code === error.TIMEOUT) {
          setLocationError('Hết thời gian chờ lấy vị trí. Vui lòng thử lại.');
          return;
        }
        setLocationError('Không thể lấy vị trí hiện tại.');
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  }, [resolvePosition]);

  useEffect(() => {
    const protocol = window.location.protocol;
    const hostname = window.location.hostname;
    const isSecure = protocol === 'https:' || hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';

    if (!isSecure) {
      setLocationError('Geolocation API yêu cầu HTTPS hoặc localhost.');
      setLocationPermission('denied');
      return;
    }

    if (navigator.permissions?.query) {
      navigator.permissions
        .query({ name: 'geolocation' })
        .then((result) => {
          setLocationPermission(result.state);
        })
        .catch(() => {
          setLocationPermission('prompt');
        });
    }

    loadProjectLocations();
  }, [loadProjectLocations]);

  return {
    location,
    locationError,
    locationPermission,
    isRequestingLocation,
    projectLocations,
    requestLocation,
    refreshLocation: requestLocation,
  };
}
